import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fetchAvailableSpots } from '../api/bookings';
import { convertDateToText } from '../utils';

function BookingModal({ open, onClose, date, onConfirm }) {
  const { t } = useTranslation();
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chargerSlotsFiltered, setChargerSlotsFiltered] = useState(false);
  const [currentChargerBookings, setCurrentChargerBookings] = useState(0);
  const [maxChargerReservations, setMaxChargerReservations] = useState(0);

  // Memoize filtered slots to prevent unnecessary re-renders
  const filteredSlots = useMemo(() => {
    return availableSlots.filter(slot => !chargerSlotsFiltered || !slot.has_charger);
  }, [availableSlots, chargerSlotsFiltered]);

  const loadAvailableSlots = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedSlot('');
      setChargerSlotsFiltered(false);
      setCurrentChargerBookings(0);
      setMaxChargerReservations(0);
      const data = await fetchAvailableSpots(date);

      if (data.blocked_by_assigned_slot) {
        setAvailableSlots([]);
        setError(t('bookings.cannot_book_assigned'));
        return;
      }

            setAvailableSlots(data.available_spots || []);
      setChargerSlotsFiltered(data.charger_slots_filtered || false);
      setCurrentChargerBookings(data.current_charger_bookings || 0);
      setMaxChargerReservations(data.max_charger_reservations || 0);

      // Set default selection to first available spot (excluding charger slots if user has reached limit)
      const tempFilteredSlots = (data.available_spots || []).filter(slot => !data.charger_slots_filtered || !slot.has_charger);

      if (tempFilteredSlots.length > 0) {
        setSelectedSlot(tempFilteredSlots[0].id);
      } else {
        setSelectedSlot('');
      }
    } catch (err) {
      setError(err.message);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  }, [date, t]);

  useEffect(() => {
    if (open && date) {
      loadAvailableSlots();
    }
  }, [open, date, loadAvailableSlots]);

  const handleConfirm = () => {
    if (selectedSlot) {
      onConfirm(date, selectedSlot);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedSlot('');
    setError(null);
    setChargerSlotsFiltered(false);
    setCurrentChargerBookings(0);
    setMaxChargerReservations(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('actions.create_booking')} - {date ? convertDateToText(date, t) : ''}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {t('general.loading')}...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : filteredSlots.length === 0 ? (
          <Alert severity="warning">
            {chargerSlotsFiltered
              ? t('bookings.no_slots_available_charger_limit', {
                  current: currentChargerBookings,
                  max: maxChargerReservations
                })
              : t('bookings.no_slots_available')
            }
          </Alert>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('bookings.select_preferred_slot')}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>{t('bookings.parking_slot')}</InputLabel>
              <Select
                value={selectedSlot}
                label={t('bookings.parking_slot')}
                onChange={(e) => setSelectedSlot(e.target.value)}
              >
                {filteredSlots.map((slot) => (
                  <MenuItem key={slot.id} value={slot.id}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <span>
                        {t('bookings.slot')} {slot.slot_number}
                      </span>
                      <Box display="flex" alignItems="center">
                        {slot.has_charger && (
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            ⚡ {t('bookings.electric_charger')}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          {slot.address}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedSlot || loading || filteredSlots.length === 0}
        >
          {t('actions.confirm_booking')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BookingModal; 